import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { IconShell } from 'module/ui/components/atoms/icon-shell';
import {
  ExpandableSearchInput,
  ExpandableSearchInputRef,
} from 'module/ui/components/atoms/input/search-input';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { HeaderWithIconTitle } from 'module/ui/components/molecules/page/page';
import { HeadingCompact01 } from 'module/ui/components/styles/typography';
import { Argon } from 'module/ui/components/templates/argon';
import {
  faBuildings,
  faColumns3,
  faFilter,
  faPlus,
  faSignal,
} from '@fortawesome/pro-solid-svg-icons';
import {
  Button,
  EmptyState as RocketlaneEmptyState,
  Icon,
} from '@rocketlane/ui';
import { Skeleton } from 'antd';
import { CompanyAPI } from 'api/company';
import { CompanyKanbanItem } from 'api/company/types';
import { SignalType, SignalConfiguration } from 'api/signal/types';
import { keyBy } from 'lodash';
import {
  useAccountSignalBoardQuery,
  useKanbanCountQuery,
} from 'queries/company';
import { useGetAllSignalConfigurationsQuery } from 'queries/signal';
import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
  VariableSizeList as VariableList,
  ListChildComponentProps,
  ListOnItemsRenderedProps,
} from 'react-window';
import { ROUTE_PATHS } from 'routes/route-paths';
import styled from 'styled-components';
import { MultiSelectSignals } from 'components/atoms/multi-select-signals';
import { signalTypeConfigs } from 'components/organisms/signal/signal-type/type-configs';
import { AccountsSignalCard } from './accounts-signal-card';
import { ViewSwitcher } from '../view-switcher';

const KANBAN_PAGE_SIZE = 20;

const ColumnContainer = styled.div`
  display: flex;
  gap: var(--spacing-04);
  height: 100%;
  width: 100%;
  background-color: var(--scarlet-gray-10);
  overflow: scroll;
  padding: 0px var(--spacing-06);
`;

const Header = styled.div`
  ${HeadingCompact01};
  margin-bottom: var(--spacing-03);
  display: flex;
  align-items: center;
`;

const CardContainer = styled.div`
  padding: var(--spacing-05);
  min-width: 300px;
  height: 300px;
  margin: var(--spacing-04);
  background-color: var(--white-100);
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    cursor: pointer;
  }
  display: flex;
`;

const CreateAccountButton = styled(Button)`
  margin-left: var(--spacing-04);
`;

const PrimaryFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-04);
`;

const SecondaryFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-04);
  flex-shrink: 1;
  min-width: 0;
  width: fit-content;
  max-width: 100%;
`;

const BoardEmptyStateRoot = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-width: 100%;
  min-height: 400px;
  padding: var(--spacing-09) var(--spacing-08);
  background-color: var(--white-100);
`;

const SkeletonLoader = () => {
  return (
    <CardContainer>
      <Skeleton active />
    </CardContainer>
  );
};

const SkeletonColumn = ({ cardCount }: { cardCount: number }) => (
  <div
    style={{
      minWidth: '300px',
    }}
  >
    {Array(cardCount)
      .fill(null)
      .map((_, index) => (
        <div key={index} style={{ marginBottom: 'var(--spacing-03)' }}>
          <SkeletonLoader />
        </div>
      ))}
  </div>
);

export const AccountsSignalBoard: React.FC<{
  isModuleFederation?: boolean;
}> = ({ isModuleFederation = true }) => {
  const navigate = useNavigate();
  const {
    value: searchQuery,
    onChange: onSearchChange,
    clear: clearSearch,
  } = useSearchValueState();
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debouncedSearchRef = useRef('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      debouncedSearchRef.current = searchQuery;
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: kanbanCount } = useKanbanCountQuery();
  const { data: initialData, isLoading: isDataLoading } =
    useAccountSignalBoardQuery({
      offset: 0,
      limit: KANBAN_PAGE_SIZE,
      ...(debouncedSearch && { search: debouncedSearch }),
    });
  const { data: signalConfigurations, isLoading: isSignalConfigsLoading } =
    useGetAllSignalConfigurationsQuery();

  const [isMultiSelectOpen, setIsMultiSelectOpen] = useState(false);
  const [selectedSignals, setSelectedSignals] = useState<number[]>([]);
  const [reorderedSignals, setReorderedSignals] = useState<
    SignalConfiguration[]
  >([]);

  const [columnData, setColumnData] = useState<
    Record<string, { columnLabel: string; items: CompanyKanbanItem[] }>
  >({});
  const [fetchingColumns, setFetchingColumns] = useState<
    Record<string, boolean>
  >({});
  const columnOffsetsRef = useRef<Record<string, number>>({});
  const fetchingColumnsRef = useRef<Record<string, boolean>>({});
  const columnExhaustedRef = useRef<Record<string, boolean>>({});

  const columnTotalCounts = useMemo(() => {
    if (!kanbanCount) {
      return {};
    }

    const counts: Record<string, number> = {};

    kanbanCount.kanbanResponseModelList.forEach((model) => {
      Object.entries(model.columnData).forEach(([colId, data]) => {
        counts[colId] = data.innerCompanyCount;
      });
    });

    return counts;
  }, [kanbanCount]);

  useEffect(() => {
    if (!initialData) {
      return;
    }

    const newColumnData: Record<
      string,
      { columnLabel: string; items: CompanyKanbanItem[] }
    > = {};
    const newOffsets: Record<string, number> = {};
    const newExhausted: Record<string, boolean> = {};

    initialData.kanbanResponseModelList.forEach((model) => {
      Object.entries(model.scarletCompanyKanbanData).forEach(([colId, col]) => {
        newColumnData[colId] = {
          columnLabel: col.columnLabel,
          items: col.items,
        };
        newOffsets[colId] = col.items.length;
        if (col.items.length < KANBAN_PAGE_SIZE) {
          newExhausted[colId] = true;
        }
      });
    });

    setColumnData(newColumnData);
    columnOffsetsRef.current = newOffsets;
    columnExhaustedRef.current = newExhausted;
  }, [initialData]);

  const fetchMoreForColumn = useCallback(
    async (colId: string) => {
      if (columnExhaustedRef.current[colId]) {
        return;
      }

      const currentOffset = columnOffsetsRef.current[colId] ?? 0;
      const totalCount = columnTotalCounts[colId] ?? 0;

      if (currentOffset >= totalCount) {
        return;
      }

      if (fetchingColumnsRef.current[colId]) {
        return;
      }

      fetchingColumnsRef.current[colId] = true;
      setFetchingColumns((prev) => ({ ...prev, [colId]: true }));

      try {
        const currentSearch = debouncedSearchRef.current;
        const response = await CompanyAPI.getAccountSignalBoard({
          colId,
          offset: currentOffset,
          limit: KANBAN_PAGE_SIZE,
          ...(currentSearch && { search: currentSearch }),
        });

        const newItems =
          response.kanbanResponseModelList[0]?.scarletCompanyKanbanData[colId]
            ?.items ?? [];

        if (newItems.length > 0) {
          columnOffsetsRef.current[colId] = currentOffset + newItems.length;
          setColumnData((prev) => ({
            ...prev,
            [colId]: {
              ...prev[colId],
              items: [...(prev[colId]?.items ?? []), ...newItems],
            },
          }));
        }

        if (newItems.length < KANBAN_PAGE_SIZE) {
          columnExhaustedRef.current[colId] = true;
        }
      } finally {
        fetchingColumnsRef.current[colId] = false;
        setFetchingColumns((prev) => ({ ...prev, [colId]: false }));
      }
    },
    [columnTotalCounts],
  );

  const handleSelectionChange = useCallback((newSelectedSignals: number[]) => {
    setSelectedSignals(newSelectedSignals);
  }, []);

  const handleSignalsReorder = useCallback(
    (newOrderedSignals: SignalConfiguration[]) => {
      setReorderedSignals(newOrderedSignals);
    },
    [],
  );

  const signalConfigurationsMap = useMemo(
    () => keyBy(signalConfigurations, 'signalConfigurationId'),
    [signalConfigurations],
  );

  const signalsToUse =
    reorderedSignals.length > 0 ? reorderedSignals : signalConfigurations || [];

  const searchInputRef = useRef<ExpandableSearchInputRef>(null);

  const filteredColumnEntries = useMemo(() => {
    const entries = Object.entries(columnData)
      .map(([colId, col]) => ({
        colId,
        columnLabel: col.columnLabel,
        items: col.items,
      }))
      .filter((col) => {
        if (selectedSignals.length === 0) {
          return col.items.length > 0;
        }

        return (
          selectedSignals.includes(Number(col.colId)) && col.items.length > 0
        );
      });

    if (reorderedSignals.length > 0) {
      const signalOrder = reorderedSignals.map((s) =>
        s.signalConfigurationId.toString(),
      );

      entries.sort((a, b) => {
        const aIndex = signalOrder.indexOf(a.colId);
        const bIndex = signalOrder.indexOf(b.colId);

        if (aIndex === -1 && bIndex === -1) {
          return 0;
        }

        if (aIndex === -1) {
          return 1;
        }

        if (bIndex === -1) {
          return -1;
        }

        return aIndex - bIndex;
      });
    }

    return entries;
  }, [columnData, selectedSignals, reorderedSignals]);

  const isLoading = isDataLoading || isSignalConfigsLoading;

  const hasNoSignalConfigurations =
    !isSignalConfigsLoading && (signalConfigurations?.length ?? 0) === 0;

  return (
    <>
      <Argon
        actions={
          hasNoSignalConfigurations
            ? {}
            : {
                primary: (
                  <PrimaryFilterContainer>
                    {!isModuleFederation && (
                      <>
                        <ViewSwitcher
                          primaryPageUrl={'/accounts'}
                          secondaryPageUrl={'/accounts/list'}
                        />
                        <Button
                          variant="secondary"
                          leftIcon={<FontAwesomeIcon icon={faFilter} />}
                        >
                          <span className="ml-1">
                            <FormattedMessage id="filters" />
                          </span>
                        </Button>
                      </>
                    )}
                  </PrimaryFilterContainer>
                ),
                secondary: (
                  <SecondaryFilterContainer>
                    <ExpandableSearchInput
                      ref={searchInputRef}
                      direction="right"
                      value={searchQuery}
                      onChange={onSearchChange}
                      searchButtonProps={{
                        type: 'text',
                      }}
                      minWidth="360px"
                      width="400px"
                    />
                    <MultiSelectSignals
                      signals={signalsToUse}
                      selectedSignals={selectedSignals}
                      onSelectionChange={handleSelectionChange}
                      onSignalsReorder={handleSignalsReorder}
                      isLoading={isLoading}
                      title="Manage signals"
                      placeholder="Search signals"
                      open={isMultiSelectOpen}
                      onOpenChange={setIsMultiSelectOpen}
                      showTrigger={true}
                      trigger={
                        <Button
                          variant="secondary"
                          leftIcon={<FontAwesomeIcon icon={faColumns3} />}
                          className="ml-2"
                        />
                      }
                    />
                    {!isModuleFederation && (
                      <CreateAccountButton
                        variant="primary"
                        leftIcon={<FontAwesomeIcon icon={faPlus} />}
                      >
                        <span className="ml-1">New Account</span>
                      </CreateAccountButton>
                    )}
                  </SecondaryFilterContainer>
                ),
              }
        }
        title={
          isModuleFederation ? (
            <></>
          ) : (
            <HeaderWithIconTitle
              icon={<FontAwesomeIcon icon={faBuildings} size="sm" />}
              title={<FormattedMessage id="accounts.page.title" />}
            />
          )
        }
        content={
          <>
            {isLoading ? (
              <div
                style={{
                  display: 'flex',
                  height: '100%',
                  backgroundColor: 'var(--scarlet-gray-10)',
                  overflowX: 'auto',
                }}
              >
                <SkeletonColumn cardCount={5} />
                <SkeletonColumn cardCount={2} />
                <SkeletonColumn cardCount={1} />
                <SkeletonColumn cardCount={3} />
                <SkeletonColumn cardCount={2} />
              </div>
            ) : (
              <ColumnContainer>
                {hasNoSignalConfigurations ? (
                  <BoardEmptyStateRoot>
                    <AccountsSignalBoardNoConfigurationsEmpty
                      onCreateSignal={() =>
                        navigate(
                          `/${ROUTE_PATHS.SETTINGS}/${ROUTE_PATHS.SETTINGS_SIGNAL_CONFIGURATION_LIST}`,
                        )
                      }
                    />
                  </BoardEmptyStateRoot>
                ) : filteredColumnEntries.length > 0 ? (
                  filteredColumnEntries.map((col) => (
                    <SignalColumn
                      key={col.colId}
                      title={col.columnLabel}
                      signals={col.items}
                      signalType={
                        signalConfigurationsMap[col.colId]?.signalType
                      }
                      isModuleFederation={isModuleFederation}
                      hasMore={
                        !columnExhaustedRef.current[col.colId] &&
                        (columnOffsetsRef.current[col.colId] ?? 0) <
                          (columnTotalCounts[col.colId] ?? 0)
                      }
                      isFetchingMore={fetchingColumns[col.colId] ?? false}
                      onFetchMore={() => fetchMoreForColumn(col.colId)}
                    />
                  ))
                ) : (
                  <BoardEmptyStateRoot>
                    <AccountsSignalBoardFilteredEmpty
                      onShowAll={() => {
                        clearSearch();
                        searchInputRef.current?.closeSearchInput();
                        setSelectedSignals([]);
                      }}
                    />
                  </BoardEmptyStateRoot>
                )}
              </ColumnContainer>
            )}
          </>
        }
      />
    </>
  );
};

interface DraggableSignalProps {
  signalName: string;
  item: CompanyKanbanItem;
  signalType: SignalType;
  isModuleFederation?: boolean;
}

const DraggableSignal: React.FC<DraggableSignalProps> = ({
  signalName,
  item,
  signalType,
  isModuleFederation,
}) => {
  return (
    <AccountsSignalCard
      signalItem={item}
      signalName={signalName}
      signalType={signalType}
      isModuleFederation={isModuleFederation}
    />
  );
};

interface SignalColumnProps {
  title: string;
  signals: CompanyKanbanItem[];
  signalType: SignalType;
  isModuleFederation?: boolean;
  hasMore: boolean;
  isFetchingMore: boolean;
  onFetchMore: () => void;
}

//overflow-x: scroll;
const DraggableSignalContainer = styled.div`
  padding-top: var(--spacing-04);
  min-height: 300px;
  border-radius: var(--border-radius-default);
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 300px;
  width: 300px;
`;
// overflow-y: scroll;
const SignalWrapper = styled.div`
  flex: 1;
  height: 100%;
`;

const IconShellWrapper = styled(IconShell)`
  color: var(--icon-interactive);
`;

const CARD_VERTICAL_GAP_PX = 12;
const DEFAULT_CARD_HEIGHT = 320 + CARD_VERTICAL_GAP_PX;

type SignalRowData = {
  signals: CompanyKanbanItem[];
  signalName: string;
  signalType: SignalType;
  registerNode: (index: number, node: HTMLDivElement | null) => void;
  isModuleFederation?: boolean;
};

const SignalRow: React.FC<ListChildComponentProps<SignalRowData>> = ({
  index,
  style,
  data,
}) => {
  const { signals, signalName, signalType, registerNode, isModuleFederation } =
    data;
  const signal = signals[index];

  const attachRef = useCallback(
    (node: HTMLDivElement | null) => {
      registerNode(index, node);
    },
    [index, registerNode],
  );

  if (!signal) {
    return null;
  }

  return (
    <div style={{ ...style, width: '100%' }}>
      <div
        ref={attachRef}
        style={{ marginBottom: `${CARD_VERTICAL_GAP_PX}px`, width: '300px' }}
      >
        <DraggableSignal
          signalName={signalName}
          item={signal}
          signalType={signalType}
          isModuleFederation={isModuleFederation}
        />
      </div>
    </div>
  );
};

const SignalColumn: React.FC<SignalColumnProps> = ({
  title,
  signals,
  signalType,
  isModuleFederation,
  hasMore,
  isFetchingMore,
  onFetchMore,
}) => {
  const listRef = useRef<VariableList>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const itemSizeMap = useRef<Record<number, number>>({});
  const resizeObserversRef = useRef<Map<number, ResizeObserver>>(new Map());
  const pendingRafRef = useRef<Map<number, number>>(new Map());
  const hasUserScrolledRef = useRef(false);

  const cancelPendingRaf = useCallback((index: number) => {
    const rafId = pendingRafRef.current.get(index);

    if (rafId !== undefined) {
      cancelAnimationFrame(rafId);
      pendingRafRef.current.delete(index);
    }
  }, []);

  const setMeasuredSize = useCallback((index: number, size: number) => {
    if (itemSizeMap.current[index] === size) {
      return;
    }

    itemSizeMap.current[index] = size;
    listRef.current?.resetAfterIndex(index, true);
  }, []);

  const disconnectObserver = useCallback(
    (index: number) => {
      cancelPendingRaf(index);
      const observer = resizeObserversRef.current.get(index);

      if (observer) {
        observer.disconnect();
        resizeObserversRef.current.delete(index);
      }
    },
    [cancelPendingRaf],
  );

  const registerNode = useCallback(
    (index: number, node: HTMLDivElement | null) => {
      disconnectObserver(index);

      if (!node) {
        return;
      }

      const measure = () => {
        if (!node.isConnected) {
          return;
        }
        const rect = node.getBoundingClientRect();
        const computedStyles = window.getComputedStyle(node);
        const marginTop = parseFloat(computedStyles.marginTop || '0');
        const marginBottom = parseFloat(computedStyles.marginBottom || '0');
        const totalHeight = rect.height + marginTop + marginBottom;

        if (totalHeight > 0) {
          setMeasuredSize(index, totalHeight);
        }
      };

      const outerRafId = requestAnimationFrame(() => {
        const innerRafId = requestAnimationFrame(() => {
          pendingRafRef.current.delete(index);

          measure();
        });

        pendingRafRef.current.set(index, innerRafId);
      });

      pendingRafRef.current.set(index, outerRafId);

      if (typeof ResizeObserver === 'undefined') {
        return;
      }

      const observer = new ResizeObserver(() => {
        measure();
      });

      observer.observe(node);
      resizeObserversRef.current.set(index, observer);
    },
    [disconnectObserver, setMeasuredSize],
  );

  const getItemSize = useCallback(
    (index: number) => itemSizeMap.current[index] ?? DEFAULT_CARD_HEIGHT,
    [],
  );

  useEffect(() => {
    itemSizeMap.current = {};
    listRef.current?.resetAfterIndex(0, true);
  }, [signals]);

  useEffect(() => {
    const observers = resizeObserversRef.current;
    const pendingRafs = pendingRafRef.current;

    return () => {
      pendingRafs.forEach((rafId) => cancelAnimationFrame(rafId));
      pendingRafs.clear();
      if (observers) {
        observers.forEach((observer) => observer.disconnect());
        observers.clear();
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const element = containerRef.current;

    const updateHeight = () => {
      setContainerHeight(element.clientHeight);
    };

    updateHeight();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        updateHeight();
      });

      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    }

    const interval = window.setInterval(updateHeight, 250);

    return () => {
      window.clearInterval(interval);
    };
  }, []);

  const handleItemsRendered = useCallback(
    ({ visibleStopIndex }: ListOnItemsRenderedProps) => {
      if (!hasUserScrolledRef.current) {
        hasUserScrolledRef.current = true;

        return;
      }

      if (
        visibleStopIndex >= signals.length - 5 &&
        hasMore &&
        !isFetchingMore
      ) {
        onFetchMore();
      }
    },
    [signals.length, hasMore, isFetchingMore, onFetchMore],
  );

  return (
    <DraggableSignalContainer>
      <Header>
        <IconShellWrapper
          size="small"
          icon={signalTypeConfigs[signalType].icon}
        />
        {title}
      </Header>
      <SignalWrapper ref={containerRef}>
        {signals.length > 0 && containerHeight > 0 ? (
          <VariableList
            height={containerHeight}
            width="100%"
            itemCount={signals.length}
            ref={listRef}
            itemSize={getItemSize}
            onItemsRendered={handleItemsRendered}
            itemData={{
              signals,
              signalName: title,
              signalType,
              registerNode,
              isModuleFederation,
            }}
          >
            {SignalRow}
          </VariableList>
        ) : null}
        {isFetchingMore && (
          <div style={{ padding: 'var(--spacing-03)', textAlign: 'center' }}>
            <Skeleton active paragraph={false} />
          </div>
        )}
      </SignalWrapper>
    </DraggableSignalContainer>
  );
};

function AccountsSignalBoardNoConfigurationsEmpty({
  onCreateSignal,
}: {
  onCreateSignal: VoidFunction;
}) {
  return (
    <RocketlaneEmptyState
      icon={{ icon: faSignal }}
      message={
        <FormattedMessage id="accounts.signal.empty.no_configurations.title" />
      }
      description={
        <FormattedMessage id="accounts.signal.empty.no_configurations.description" />
      }
      primaryAction={{
        leftIcon: <Icon icon={faPlus} size=".75x" />,
        onClick: onCreateSignal,
        children: (
          <FormattedMessage id="settings.signal_configuration.action.create_signal" />
        ),
      }}
    />
  );
}

function AccountsSignalBoardFilteredEmpty({
  onShowAll,
}: {
  onShowAll: VoidFunction;
}) {
  return (
    <RocketlaneEmptyState
      icon={{ icon: faSignal }}
      message={<FormattedMessage id="accounts.signal.empty.filtered.title" />}
      secondaryAction={{
        variant: 'secondary',
        onClick: onShowAll,
        children: <FormattedMessage id="accounts.signal.empty.filtered.cta" />,
      }}
    />
  );
}
